import { MinusCircleIcon } from '@heroicons/react/solid';
import { format } from 'date-fns';
import { useState, useEffect, useMemo } from 'react';
import { Input, Label } from '../Field';

const EMPTY_ARRAY = [];

function normalizeGaps(gaps = []) {
    return gaps
        .filter((gap) => gap && gap.start && gap.end)
        .reduce((normalizedGaps, { start, end, baseline_index }) => {
            const normalizedGap = {
                start: format(new Date(start), "yyyy-MM-dd'T'HH:mm"),
                end: format(new Date(end), "yyyy-MM-dd'T'HH:mm"),
            };

            if (baseline_index !== undefined && baseline_index !== null && baseline_index !== '') {
                normalizedGaps[parseInt(baseline_index)] = normalizedGap;
            } else {
                normalizedGaps.push(normalizedGap);
            }

            return normalizedGaps;
        }, []);
}

function restoreDeletedBaselineGaps(gaps, baselineGaps) {
    const restoredGaps = [];

    for (let index = 0; index < Math.max(gaps.length, baselineGaps.length); index++) {
        if (gaps[index]) {
            restoredGaps.push(gaps[index]);
        } else if (baselineGaps[index]) {
            restoredGaps.push({ ...baselineGaps[index], deleted: true });
        }
    }

    return restoredGaps;
}

function cloneGaps(gaps) {
    return gaps.map((gap) => ({ ...gap }));
}

function gapsMatchEntry(a, b) {
    return (a?.start || '') === (b?.start || '') && (a?.end || '') === (b?.end || '');
}

function isDeletedGap(gap) {
    return !!gap?.deleted;
}

function hasInvalidDateRange({ start, end }) {
    return !!start && !!end && end <= start;
}

function GapsTab({ gaps: propGaps = EMPTY_ARRAY, baselineGaps: propBaselineGaps = EMPTY_ARRAY, t }) {
    const baselineGaps = useMemo(() => normalizeGaps(propBaselineGaps || []), [propBaselineGaps]);
    const [gaps, setGaps] = useState(() => restoreDeletedBaselineGaps(normalizeGaps(propGaps || []), baselineGaps));

    const updateGap = (index, field, value) => {
        setGaps((prev) => {
            const next = cloneGaps(prev);
            next[index][field] = value;
            return next;
        });
    };

    const removeGap = (index) => {
        setGaps((prev) => {
            const next = cloneGaps(prev);

            if (baselineGaps[index]) {
                next[index].deleted = true;
            } else {
                next.splice(index, 1);
            }

            return next;
        });
    };

    const discardGapChange = (index) => {
        setGaps((prev) => {
            const next = cloneGaps(prev);
            const baselineGap = baselineGaps[index];

            if (baselineGap) {
                next[index] = { ...baselineGap };
            } else {
                next.splice(index, 1);
            }

            return next;
        });
    };

    useEffect(() => {
        setGaps(restoreDeletedBaselineGaps(normalizeGaps(propGaps || []), baselineGaps));
    }, [propGaps, baselineGaps]);

    const changedInputClasses = 'border-amber-400 bg-amber-50 ring-1 ring-amber-200 focus:border-amber-500 focus:ring-amber-500';
    const invalidInputClasses = 'border-red-400 bg-red-50 ring-1 ring-red-200 focus:border-red-500 focus:ring-red-500';

    return (
        <div className="space-y-2">
            {gaps.every(isDeletedGap) && <input type="hidden" name="gaps[]" />}
            {gaps.map(({ start, end, deleted }, index) => {
                const baselineGap = baselineGaps[index];
                const startChanged = (start || '') !== (baselineGap?.start || '');
                const endChanged = (end || '') !== (baselineGap?.end || '');
                const gapChanged = deleted || !gapsMatchEntry({ start, end }, baselineGap);
                const invalidDateRange = hasInvalidDateRange({ start, end });
                const submittedGapIndex = gaps.slice(0, index).filter((gap) => !isDeletedGap(gap)).length;

                if (deleted) {
                    return (
                        <div key={index} className="flex items-center gap-8 text-xs text-amber-700">
                            <div className="flex items-center gap-2 rounded-md border border-dashed border-amber-300 bg-amber-50 px-2 py-1">
                                <span className="font-medium">{t('start')}</span>
                                <span className="line-through">{start}</span>
                                <span className="font-medium">{t('end')}</span>
                                <span className="line-through">{end}</span>
                            </div>
                            <div className="ml-2 w-24 flex-none">
                                <button
                                    onClick={() => {
                                        discardGapChange(index);
                                    }}
                                    type="button"
                                    className="text-xs font-medium text-amber-700 hover:text-amber-900"
                                >
                                    {t('discard-change')}
                                </button>
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={index} className="flex items-start gap-8">
                        <div className="flex items-center gap-2">
                            <Label>{t('start')}</Label>
                            <input type="hidden" name={`_gap_baseline_indexes[${submittedGapIndex}]`} value={index} readOnly />
                            <input type="hidden" name={`gaps[${submittedGapIndex}][start]`} value={start ? new Date(start).toISOString() : ''} readOnly />
                            <Input
                                type="datetime-local"
                                className={`${startChanged ? changedInputClasses : ''} w-48 py-1 text-xs`}
                                value={start}
                                onChange={(e) => {
                                    updateGap(index, 'start', e.target.value);
                                }}
                                title={startChanged ? t('changed-field') : undefined}
                                required
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <Label>{t('end')}</Label>
                                <input type="hidden" name={`gaps[${submittedGapIndex}][end]`} value={end ? new Date(end).toISOString() : ''} readOnly />
                                <Input
                                    type="datetime-local"
                                    className={`${invalidDateRange ? invalidInputClasses : endChanged ? changedInputClasses : ''} w-48 py-1 text-xs`}
                                    value={end}
                                    onChange={(e) => {
                                        updateGap(index, 'end', e.target.value);
                                    }}
                                    ref={(input) => {
                                        input?.setCustomValidity(invalidDateRange ? t('end-after-start') : '');
                                    }}
                                    title={invalidDateRange ? t('end-after-start') : endChanged ? t('changed-field') : undefined}
                                    required
                                />
                            </div>
                            {invalidDateRange && <p className="mt-1 text-xs text-red-600">{t('end-after-start')}</p>}
                        </div>
                        <div className="ml-2 flex w-24 flex-none items-center gap-2">
                            <button
                                onClick={() => {
                                    removeGap(index);
                                }}
                                type="button"
                                className="text-gray-400 hover:text-gray-700"
                            >
                                <MinusCircleIcon className="h-5 w-5" />
                            </button>
                            {gapChanged && (
                                <button
                                    onClick={() => {
                                        discardGapChange(index);
                                    }}
                                    type="button"
                                    className="text-xs font-medium text-amber-700 hover:text-amber-900"
                                >
                                    {t('discard-change')}
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}

            <button
                onClick={() => {
                    setGaps((prev) => [...cloneGaps(prev), { start: '', end: '' }]);
                }}
                type="button"
                className="text-xs text-gray-500 hover:text-gray-800"
            >
                + {t('add-gap')}
            </button>
        </div>
    );
}

export default GapsTab;
